import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { formatDate } from '@angular/common';

// Import custom services
import { UserAuthenticationService } from '../google/user-authentication.service';
import { LanguageService } from '../language/language.service';
import { UtilsService } from '../utils/utils.service';

// Import global variables
import { apiEndpoints } from '../../constants/global';


@Injectable({
  providedIn: 'root'
})
export class ControllerService {


  // This variable is only used to display some widgets that are not relevant from the security point of view
  public userIsLogged:boolean = false


  constructor(
    public userAuthenticationService:UserAuthenticationService,
    public languageService:LanguageService,
    private utilsService:UtilsService,
    private router: Router,
  ) { }


  // Initialization method
  initializeApp(){
    // Set the default language (This is executed before login)
    this.languageService.setLanguageAccordingToNavigatorLanguage()
    // Subscribe to the user login state
    this.subscribeToUserLoginState()
  }



  // ------ Navigation actions ----//

  // Method for subscribing to the user authentication service and checking if the user is logged in or not
  // Navigate to the login or landing page accordingly  
  async subscribeToUserLoginState(){
    await this.userAuthenticationService.getUserIsLoggedValue().subscribe(async (userIsLogged:boolean) => {
      if(userIsLogged){
        // Go to landing page
        this.router.navigate(['/']);
        // Store the variable value
        this.userIsLogged = true
      }else{
        // Go to login page
        this.router.navigate(['/login']);
        // Store the variable value
        this.userIsLogged = false
      }
    });
  }

  navigate(route:string){
    this.router.navigate([route])
  }





  // ------ API Requests (Individual users) ----//

  async newUser_individual(email:string, region:string, languageCode:string){
    const urlString = apiEndpoints.individualUSers.newUser
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      email: email,
      languageCode: languageCode,
      region: region,
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async listUsers_individual(region:string){
    const urlString = apiEndpoints.individualUSers.listUsers
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async getUser_individual(email:string, region:string){}

  async updateUser_individual(email:string, region:string, languageCode:string){}

  async deleteUser_individual(email:string, region:string){
    const urlString = apiEndpoints.individualUSers.deleteUser
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      email: email,
      region: region
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }





  // ------ API Requests (Corporate users) ----//

  async newDomain_corporate(adminEmail:string, region:string, languageCode:string, nDays:number, dbCollaborator:boolean, 
                            notificationEmail:string, emailPasswordAuth:boolean, passwordRotation:number){
    const domain = adminEmail.split('@')[1]
    const adminUser = adminEmail.split('@')[0]
    const urlString = apiEndpoints.corporateUSers.newDomain
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      adminUser: adminUser,
      days: nDays,
      domainVerified: true,
      notificationEmail: notificationEmail,
      languageCode: languageCode,
      generateJsonFile: true,
      dbCollaborator: dbCollaborator,
      emailPasswordAuth: emailPasswordAuth,
      passwordRotation: passwordRotation,
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }
  
  async listDomains_corporate(region:string){
    const urlString = apiEndpoints.corporateUSers.listDomains
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async getDomain_corporate(region:string, domain:string){
    const urlString = apiEndpoints.corporateUSers.getDomain
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async updateDomain_corporate(domain:string, region:string, languageCode:string, endingTimestamp:string, dbCollaborator:boolean, 
                              notificationEmail:string, passwordRotation:number){
    const urlString = apiEndpoints.corporateUSers.updateDomain
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      endingTimestamp: endingTimestamp,
      notificationEmail: notificationEmail,
      languageCode: languageCode,
      dbCollaborator: dbCollaborator,
      passwordRotation: passwordRotation,
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async deleteDomain_corporate(region:string, domain:string){
    const urlString = apiEndpoints.corporateUSers.deleteDomain
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }



  async newUser_corporate(region:string, domain:string, username:string, roles:string[]){
    const urlString = apiEndpoints.corporateUSers.newUser
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      username: username,
      user: roles.includes('user'),
      admin: roles.includes('admin'),
      owner: roles.includes('owner')
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async updateUser_corporate(region:string, domain:string, username:string, roles:string[]){
    const urlString = apiEndpoints.corporateUSers.updateUser
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      username: username,
      user: roles.includes('user'),
      admin: roles.includes('admin'),
      owner: roles.includes('owner')
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async deleteUser_corporate(region:string, domain:string, username:string){
    const urlString = apiEndpoints.corporateUSers.deleteUser
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      username: username,
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }



  // ------ API Requests (Analysis count) ----//

  async analysisCount_userAnalysisCount(region:string, email:string, startDate:string, endDate:string){
    const urlString = apiEndpoints.analysisCount.userAnalysisCount
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      email: email,
      startDate: startDate,
      endDate: endDate
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async analysisCount_domainAnalysisCount(region:string, domain:string, startDate:string, endDate:string){
    const urlString = apiEndpoints.analysisCount.domainAnalysisCount
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      startDate: startDate,
      endDate: endDate
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async analysisCount_globalAnalysisCount(region:string, startDate:string, endDate:string){
    const urlString = apiEndpoints.analysisCount.globalAnalysisCount
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      startDate: startDate,
      endDate: endDate
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }

  async analysisCount_domainActivity(region:string, domain:string, startDate:string, endDate:string){
    const urlString = apiEndpoints.analysisCount.domainActivity
    const authorizationToken = await this.userAuthenticationService.getUserIdToken();
    const jsonData = {
      region: region,
      domain: domain,
      startDate: startDate,
      endDate: endDate
    }
    const result = await this.utilsService.executeHttpPostMethod(urlString, jsonData, authorizationToken) 
    return result
  }



}
